import React from 'react';
import PropTypes from 'prop-types';

import {useLocation, useSearchParams} from 'react-router-dom';
import posthog from 'posthog-js';

import {dispatch, connect} from 'utils/state_manager';

const PostHogSync = (props) => {
  const user = props.user;
  const telemetryQueue = props.telemetryQueue || [];


  const [params] = useSearchParams();

  const initializedRef = React.useRef(false);
  const processedIdsRef = React.useRef(new Set());

  const posthogKey = process.env.REACT_APP_POSTHOG_KEY;

  const location = useLocation();
  const isPostHogEnabled = Boolean(posthogKey);

  React.useEffect(() => {
    if (!isPostHogEnabled || initializedRef.current) {
      return;
    }

    const distinctId = params.get('distinct_id');
    const sessionId = params.get('session_id');

    posthog.init(posthogKey, {
      api_host: 'https://us.i.posthog.com',
      loaded: (ph) => {
        if (process.env.NODE_ENV === 'development') {
          ph.opt_out_capturing();
        }
      },
      bootstrap: {
        sessionID: sessionId,
        distinctID: distinctId,
      },
    });

    initializedRef.current = true;
  }, [isPostHogEnabled, posthogKey]);

  React.useEffect(() => {
    if (!isPostHogEnabled || !initializedRef.current) {
      return;
    }

    if (!user?.userRn) {
      posthog.reset();
      return;
    }

    posthog.identify(user.userRn, {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      userRn: user.userRn,
      clientRn: user.clientRn,
    });
  }, [user, isPostHogEnabled]);

  React.useEffect(() => {
    if (!isPostHogEnabled || !initializedRef.current) {
      return;
    }

    posthog.capture('$pageview');
  }, [location.pathname, location.search, isPostHogEnabled]);

  React.useEffect(() => {
    if (!isPostHogEnabled || !initializedRef.current) {
      return;
    }

    if (!telemetryQueue.length) {
      processedIdsRef.current.clear();
      return;
    }

    telemetryQueue.forEach((event) => {
      if (processedIdsRef.current.has(event.id)) {
        return;
      }

      posthog.capture(event.name, event.properties);
      processedIdsRef.current.add(event.id);
    });
  }, [telemetryQueue, isPostHogEnabled]);

  return null;
};

PostHogSync.propTypes = {
  telemetryQueue: PropTypes.array,
  user: PropTypes.object,
};

const mapStateToProps = (state) => ({
  telemetryQueue: state.telemetry?.queue || [],
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(PostHogSync));
